import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { AdminPanelSettings as AdminPanelSettingsIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  getIdentityConfig,
  getIdentityOidcAuthorizeUrl,
  getIdentityProvisionPreview,
  runIdentityScimProvision,
  updateIdentityConfig
} from 'api/securityOps';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };
const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

const parseError = (error, fallback) => {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  return payload.detail || fallback;
};

const toJson = (value) => JSON.stringify(value || {}, null, 2);

const AdminIdentityConfigPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [preview, setPreview] = useState(null);
  const [oidcAuthorize, setOidcAuthorize] = useState('');
  const [scimPayload, setScimPayload] = useState('[\n  {\n    "email": "user@example.com",\n    "first_name": "Analyst",\n    "last_name": "One",\n    "role": "analyst"\n  }\n]');
  const [provisioning, setProvisioning] = useState(false);
  const [form, setForm] = useState({
    oidc: '{}',
    scim: '{}',
    role_templates: '{}'
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const [config, provision] = await Promise.all([getIdentityConfig(), getIdentityProvisionPreview()]);
      const authorize = await getIdentityOidcAuthorizeUrl().catch(() => null);
      setForm({
        oidc: toJson(config?.oidc),
        scim: toJson(config?.scim),
        role_templates: toJson(config?.role_templates)
      });
      setPreview(provision || null);
      setOidcAuthorize(authorize?.authorize_url || '');
    } catch (err) {
      setError(parseError(err, 'Failed to load identity configuration.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        oidc: JSON.parse(form.oidc || '{}'),
        scim: JSON.parse(form.scim || '{}'),
        role_templates: JSON.parse(form.role_templates || '{}')
      };
      const updated = await updateIdentityConfig(payload);
      setForm({
        oidc: toJson(updated?.oidc),
        scim: toJson(updated?.scim),
        role_templates: toJson(updated?.role_templates)
      });
      setSuccess('Identity configuration updated successfully.');
      const provision = await getIdentityProvisionPreview();
      setPreview(provision || null);
    } catch (err) {
      setError(parseError(err, 'Failed to save identity configuration.'));
    } finally {
      setSaving(false);
    }
  };

  const onScimProvision = async () => {
    setProvisioning(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        users: JSON.parse(scimPayload || '[]'),
        dry_run: false
      };
      const result = await runIdentityScimProvision(payload);
      setSuccess(`SCIM provisioning completed: ${result?.created || 0} created, ${result?.updated || 0} updated.`);
      const provision = await getIdentityProvisionPreview();
      setPreview(provision || null);
    } catch (err) {
      setError(parseError(err, 'Failed to run SCIM provisioning.'));
    } finally {
      setProvisioning(false);
    }
  };

  const usersByRole = preview?.users_by_role || {};

  return (
    <Stack spacing={2.5}>
      <Alert severity="warning">
        Single sign-on sign-in is not available yet. This page saves your OIDC and SCIM settings and can preview provisioning, but people still sign in with
        their email and password.
      </Alert>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <AdminPanelSettingsIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Identity Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure OIDC/SCIM settings and role mapping templates for enterprise SSO provisioning.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={load} disabled={loading || saving} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={onSave} disabled={saving || loading} sx={actionButtonSx}>
                {saving ? 'Saving...' : 'Save Config'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="OIDC Configuration (JSON)"
                    multiline
                    minRows={6}
                    fullWidth
                    value={form.oidc}
                    onChange={(event) => setForm((prev) => ({ ...prev, oidc: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="SCIM Configuration (JSON)"
                    multiline
                    minRows={6}
                    fullWidth
                    value={form.scim}
                    onChange={(event) => setForm((prev) => ({ ...prev, scim: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Role Templates (JSON)"
                    multiline
                    minRows={6}
                    fullWidth
                    value={form.role_templates}
                    onChange={(event) => setForm((prev) => ({ ...prev, role_templates: event.target.value }))}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={2}>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Provision Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Active users: {preview?.active_users ?? 0}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell align="right">Users</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(usersByRole).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="caption" color="text.secondary">
                            No role distribution available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      Object.entries(usersByRole).map(([role, count]) => (
                        <TableRow key={role}>
                          <TableCell>{role}</TableCell>
                          <TableCell align="right">{count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  OIDC Authorization URL
                </Typography>
                <TextField size="small" fullWidth value={oidcAuthorize} InputProps={{ readOnly: true }} placeholder="Configure OIDC to generate URL" />
              </CardContent>
            </Card>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  SCIM Provisioning
                </Typography>
                <Stack spacing={1.2}>
                  <TextField
                    label="SCIM Users JSON"
                    multiline
                    minRows={5}
                    value={scimPayload}
                    onChange={(event) => setScimPayload(event.target.value)}
                  />
                  <Button variant="contained" onClick={onScimProvision} disabled={provisioning} sx={actionButtonSx}>
                    {provisioning ? 'Provisioning...' : 'Run SCIM Provision'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AdminIdentityConfigPage;
